import { ref } from 'vue';
import axios from 'axios';
import functions from '@/use/functions';
import { Draw } from '@/models/loans';
import { FormChangesResponse } from "@/models/form";
import { $api } from '@/services/api1';

export function useDrawActions(loanId: number, onSuccessAsync: () => Promise<void>) {
	const isSubmitting = ref<boolean>(false);

	async function createDrawAsync(formData: Record<string, any>) {
		isSubmitting.value = true;

		// sanitize requested amount
		formData.requestedAmount = String(formData.requestedAmount).replace(/[$,]/g, '');
		
		try {
			const request = {
				loanId: loanId,
				...formData
			};

			await axios.post(`/api/loanservice/loan/${loanId}/draw`, request);

			await onSuccessAsync();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue creating a draw. Please try again later.'
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	async function updateDrawAsync(formData: Record<string, any>, draw: Draw | null, formChanges: FormChangesResponse) {
		if (!draw) {
			return;
		}

		isSubmitting.value = true;

		try {
			// sanitize requested amount
			formData.requestedAmount = String(formData.requestedAmount).replace(/[$,]/g, '');

			const request = {
				id: draw.id,
				certifiedAmount: draw.certifiedAmount,
				...formData
			};

			await axios.patch(`/api/loanservice/draws/${draw.id}`, request);

			await $api.comments.addLoanComment({
      	loanId: loanId,
      	comment: formChanges.comment,
      	userDisplayName: formChanges.userDisplayName,
    	});

			await onSuccessAsync();
		} catch (error) {
			const message = 'Please try again or reach out to SLC_Support for assistance.';

			await $api.comments.addLoanComment({
				loanId: loanId,
				comment: `${formChanges.commentPrefix} ${formChanges.errorMessage}`,
				userDisplayName: formChanges.userDisplayName,
			});

			const errorMessage =  formChanges.changes?.length > 0 && formChanges.errorMessage 
				? `${formChanges.errorMessage} ${message}` 
				: `There was an issue updating draw. ${message}`;
			
			functions.openModal({
				title: `Oops!`,
				description: errorMessage
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	return {
		isSubmitting,
		createDrawAsync,
		updateDrawAsync
	};
}
